// src/components/ScheduleTable.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTranslation } from "react-i18next";
import P2ActionsBtns from "../components/P2ActionsBtns";
import ShiftLegend from "../components/ShiftLegend";
import ShiftDisplayOptions from "../components/ShiftDisplayOptions";
import { History } from "@mui/icons-material";

const ScheduleTable = ({
  participants,
  schedule,
  days,
  holidays,
  isMobile,
  openEditParticipant,
  toggleHoliday,
  handleCellRightClick,
  handleMouseDownLeft,
  handleMouseEnterLeft,
  handleMouseUpLeft,
  totalHolidayShifts,
  shifts,
  handleClear,
  handleClearAll,
  handleAutocomplete,
  handleExport,
  onOpenParticipantHistory,
  onReorderParticipants,
  toggleUnavailableCell,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [shiftDisplay, setShiftDisplay] = useState("initials");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const tableRef = useRef(null);

  const STICKY_BG = "#f0f0f0";
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastTapCell, setLastTapCell] = useState(null);
  const singleTapTimer = useRef(null);
  const DOUBLE_TAP_DELAY = 300; // milliseconds

  // 10-color palette for shifts (no black, red, or yellow)
  const shiftColors = [
    "#8A2BE2", // Blue Violet
    "#45B7D1", // Blue
    "#DDA0DD", // Plum
    "#87CEEB", // Sky Blue
    "#4ECDC4", // Teal
    "#96CEB4", // Green
    "#FFB347", // Orange
    "#FF7F50", // Coral
    "#98D8C8", // Mint
    "#20B2AA", // Light Sea Green
  ];

  // Dynamic column widths based on expanded state
  const columnsWidths = useMemo(() => {
    if (isExpanded) {
      // In fullscreen mode, distribute extra width
      const baseNameWidth = 100;
      const extraNameWidth = 30;
      const baseDayWidth = 15;

      // Calculate extra width for day columns
      // Assuming we have more space in fullscreen, distribute it evenly among day columns
      const extraWidthPerDay = Math.max(15, Math.floor(30 / days)); // At least 10px extra per day

      return {
        drag: 20, // New drag handle column
        edit: 15,
        name: baseNameWidth + extraNameWidth,
        total: 55,
        holiday: 55,
        days: baseDayWidth + extraWidthPerDay,
      };
    } else {
      // Normal mode - original widths
      return {
        drag: 30, // New drag handle column
        edit: 20,
        name: 100,
        total: 55,
        holiday: 55,
        days: 15,
      };
    }
  }, [isExpanded, days]);

  const stickyOffsets = useMemo(
    () => ({
      drag: 0,
      edit: columnsWidths.drag,
      name: columnsWidths.drag + columnsWidths.edit,
      total: columnsWidths.drag + columnsWidths.edit + columnsWidths.name,
      holiday:
        columnsWidths.drag +
        columnsWidths.edit +
        columnsWidths.name +
        columnsWidths.total,
    }),
    [columnsWidths],
  );

  // Function to get color for a shift
  const getShiftColor = (shiftName) => {
    if (!shiftName || shiftName === "" || shiftName === "-") {
      return "transparent";
    }

    const shiftIndex = shifts.findIndex((s) => s.name === shiftName);
    if (shiftIndex === -1) return "transparent";

    // Use modulo to cycle through colors if there are more than 10 shifts
    return shiftColors[shiftIndex % shiftColors.length];
  };

  // Function to get contrasting text color
  const getTextColor = (backgroundColor) => {
    if (backgroundColor === "transparent") return "black";

    // Convert hex to RGB
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.6 ? "black" : "white";
  };

  const getShiftLabel = (tableCell) => {
    if (tableCell === "" || tableCell === "-") {
      return tableCell;
    }
    const shift = shifts.find((s) => s.name === tableCell);
    if (!shift) {
      return tableCell;
    }
    switch (shiftDisplay) {
      case "initials":
        return shift.name
          .split(" ")
          .map((word) => word[0])
          .join("")
          .toUpperCase();
      case "numbers":
        return shift.id;
      default:
        return shift.name;
    }
  };

  const totalMaxShifts = useMemo(() => {
    return participants.reduce(
      (acc, participant) => acc + participant.maxShiftsPerPerson,
      0,
    );
  }, [participants]);

  const participantData = useMemo(() => {
    return participants.map((person, pIndex) => {
      const totalShifts = Object.values(schedule[pIndex]).filter(
        (val) => val !== "" && val !== "-",
      ).length;
      const holidayShifts = Object.entries(schedule[pIndex]).filter(
        ([dayIndex, val]) =>
          val !== "" && val !== "-" && holidays.includes(Number(dayIndex)),
      ).length;

      return {
        ...person,
        totalShifts,
        holidayShifts,
      };
    });
  }, [participants, schedule, holidays]);

  const dailyTotals = useMemo(() => {
    return [...Array(days)].map((_, dayIndex) => {
      return participantData.reduce(
        (acc, _, pIndex) =>
          acc +
          (schedule[pIndex][dayIndex] !== "" &&
          schedule[pIndex][dayIndex] !== "-"
            ? 1
            : 0),
        0,
      );
    });
  }, [days, participantData, schedule]);

  const totalRowIsFull = useMemo(() => {
    return dailyTotals.every(
      (dayTotal, dayIndex) => dayTotal === shifts[dayIndex],
    );
  }, [dailyTotals, shifts]);

  const totalAllShifts = useMemo(() => {
    return participantData.reduce((acc, person) => acc + person.totalShifts, 0);
  }, [participantData]);

  const stickyColumnStyle = (leftOffset) => ({
    position: "sticky",
    backgroundColor: STICKY_BG,
    left: leftOffset,
    zIndex: 2,
  });

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      // Call the parent component's reorder function
      onReorderParticipants(draggedIndex, dropIndex);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isExpanded]);

  return (
    <Box
      sx={{
        overflow: "hidden",
        backgroundColor: "#F5F5F5",
        borderRadius: "12px",
        p: 2,
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        border: "2px solid #ccc",
        transition:
          "transform 0.4s ease, border-radius 0.3s ease, box-shadow 0.3s ease",
        transform: isExpanded
          ? "scale(1) translate(0, 0)"
          : "scale(0.95) translate(0, 20px)",
        opacity: isExpanded ? 1 : 0.9,
        ...(isExpanded && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          p: 0,
          borderRadius: 0,
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
        }),
      }}
    >
      <ShiftDisplayOptions
        shiftDisplay={shiftDisplay}
        setShiftDisplay={setShiftDisplay}
        t={t}
      />
      <br />
      {/* New Shift Legend */}
      <Box display="flex" justifyContent="center">
        <ShiftLegend shifts={shifts} t={t} />
      </Box>
      {/* Actions Btns */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          p: isExpanded ? 2 : 0,
        }}
      >
        <P2ActionsBtns
          handleClear={handleClear}
          handleClearAll={handleClearAll}
          handleAutocomplete={handleAutocomplete}
          handleExport={handleExport}
        />
        <Button
          variant="outlined"
          startIcon={isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? t("Exit") : t("Fullscreen")}
        </Button>
      </Box>
      {/* Table */}
      <Box
        style={{
          overflow: "auto",
          flex: isExpanded ? 1 : "initial",
          padding: isExpanded ? "0 16px 16px" : 0,
        }}
      >
        <Table
          ref={tableRef}
          id="schedule-table"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
            mt: 2,
            "& .MuiTableCell-root": {
              border: "1px solid #ddd",
              padding: "4px",
            },
          }}
        >
          <TableHead sx={{ position: "sticky", top: 0, zIndex: 10 }}>
            <TableRow>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.drag),
                  width: columnsWidths.drag,
                  minWidth: columnsWidths.drag,
                  maxWidth: columnsWidths.drag,
                  textAlign: "center",
                }}
              >
                {/* Drag handle header - empty */}
              </TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.edit),
                  width: columnsWidths.edit,
                  minWidth: columnsWidths.edit,
                  maxWidth: columnsWidths.edit,
                  textAlign: "center",
                }}
              ></TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.name),
                  width: columnsWidths.name,
                  minWidth: columnsWidths.name,
                  maxWidth: columnsWidths.name,
                  textAlign: "center",
                  padding: "2px",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<History fontSize="small" />}
                  onClick={onOpenParticipantHistory}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    px: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "white",
                      transform: "translateY(-1px)",
                      boxShadow: 2,
                    },
                  }}
                  title={t("Click to add participants from history")}
                >
                  {t("Name")}
                </Button>
              </TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.total),
                  width: columnsWidths.total,
                  minWidth: columnsWidths.total,
                  maxWidth: columnsWidths.total,
                  textAlign: "center",
                }}
              >
                {t("total")}
              </TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.holiday),
                  width: columnsWidths.holiday,
                  minWidth: columnsWidths.holiday,
                  maxWidth: columnsWidths.holiday,
                  textAlign: "center",
                }}
              >
                {t("holiday")}
              </TableCell>
              {[...Array(days)].map((_, i) => (
                <TableCell
                  key={i}
                  onClick={() => toggleHoliday(i)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: holidays.includes(i) ? "#ffeb3b" : "transparent",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    width: columnsWidths.days,
                    minWidth: columnsWidths.days,
                    maxWidth: columnsWidths.days,
                    textAlign: "center",
                  }}
                  title={t("Click to toggle holiday")}
                >
                  {i + 1}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {participantData.map((person, pIndex) => (
              <TableRow
                key={pIndex}
                draggable
                onDragStart={(e) => handleDragStart(e, pIndex)}
                onDragOver={(e) => handleDragOver(e, pIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, pIndex)}
                onDragEnd={handleDragEnd}
                sx={{
                  opacity: draggedIndex === pIndex ? 0.5 : 1,
                  backgroundColor:
                    dragOverIndex === pIndex
                      ? "rgba(0, 0, 0, 0.1)"
                      : "transparent",
                  transition: "all 0.2s ease",
                  cursor: "move",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <TableCell
                  style={{
                    ...stickyColumnStyle(stickyOffsets.drag),
                    width: columnsWidths.drag,
                    minWidth: columnsWidths.drag,
                    maxWidth: columnsWidths.drag,
                    textAlign: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{
                      cursor: "grab",
                      "&:active": { cursor: "grabbing" },
                      color: "text.secondary",
                    }}
                    title={t("Drag to reorder")}
                  >
                    <DragIndicatorIcon />
                  </IconButton>
                </TableCell>
                <TableCell
                  style={{
                    ...stickyColumnStyle(stickyOffsets.edit),
                    width: columnsWidths.edit,
                    minWidth: columnsWidths.edit,
                    maxWidth: columnsWidths.edit,
                    textAlign: "center",
                  }}
                >
                  <EditIcon
                    onClick={() => openEditParticipant(pIndex)}
                    sx={{ cursor: "pointer" }}
                    title={t("clickToEditParticipant")}
                  />
                </TableCell>
                <TableCell
                  style={{
                    ...stickyColumnStyle(stickyOffsets.name),
                    width: columnsWidths.name,
                    minWidth: columnsWidths.name,
                    maxWidth: columnsWidths.name,
                  }}
                >
                  {person.name} ({t("max")}: {person.maxShiftsPerPerson})
                </TableCell>
                <TableCell
                  style={{
                    ...stickyColumnStyle(stickyOffsets.total),
                    width: columnsWidths.total,
                    minWidth: columnsWidths.total,
                    maxWidth: columnsWidths.total,
                    textAlign: "center",
                  }}
                >
                  {person.totalShifts}
                </TableCell>
                <TableCell
                  style={{
                    ...stickyColumnStyle(stickyOffsets.holiday),
                    width: columnsWidths.holiday,
                    minWidth: columnsWidths.holiday,
                    maxWidth: columnsWidths.holiday,
                    textAlign: "center",
                  }}
                >
                  {person.holidayShifts}
                </TableCell>
                {[...Array(days)].map((_, dayIndex) => {
                  const cellValue = schedule[pIndex][dayIndex];
                  const isAssigned = cellValue !== "" && cellValue !== "-";
                  const isUnavailable = cellValue === "-";
                  const isHoliday = holidays.includes(dayIndex);

                  let bgColor;
                  let textColor;

                  if (isAssigned) {
                    bgColor = getShiftColor(cellValue);
                    textColor = getTextColor(bgColor);
                  } else if (isUnavailable) {
                    bgColor = "rgba(250, 92, 92, 0.69)";
                    textColor = "black";
                  } else if (isHoliday) {
                    bgColor = "#FFEAA7";
                    textColor = "black";
                  } else {
                    bgColor = "white";
                    textColor = "black";
                  }

                  const handleMobileTouch = (e) => {
                    e.preventDefault(); // Prevent zoom and other default behaviors

                    const currentTime = new Date().getTime();
                    const cellId = `${pIndex}-${dayIndex}`;

                    // Clear any pending single tap timer
                    if (singleTapTimer.current) {
                      clearTimeout(singleTapTimer.current);
                      singleTapTimer.current = null;
                    }

                    // Check if this is a double tap
                    if (
                      currentTime - lastTapTime < DOUBLE_TAP_DELAY &&
                      lastTapCell === cellId
                    ) {
                      // Double tap - assign shift
                      handleCellRightClick(e, pIndex, dayIndex);
                      setLastTapTime(0); // Reset to prevent triple tap
                      setLastTapCell(null);
                    } else {
                      // Single tap - set timer to execute after delay
                      setLastTapTime(currentTime);
                      setLastTapCell(cellId);

                      // Capture the current pIndex and dayIndex values
                      const currentPIndex = pIndex;
                      const currentDayIndex = dayIndex;

                      // Set timer for single tap action - directly call the toggle function
                      singleTapTimer.current = setTimeout(() => {
                        // Call the toggle function directly instead of going through handleMouseDownLeft
                        toggleUnavailableCell(currentPIndex, currentDayIndex);
                        singleTapTimer.current = null;
                      }, DOUBLE_TAP_DELAY);
                    }
                  };

                  return (
                    <TableCell
                      key={dayIndex}
                      onContextMenu={(e) => {
                        if (!isMobile) {
                          handleCellRightClick(e, pIndex, dayIndex);
                        }
                      }}
                      onDoubleClick={(e) => {
                        if (!isMobile) {
                          // Handle double-click only for desktop
                          handleCellRightClick(e, pIndex, dayIndex);
                        }
                      }}
                      onMouseDown={(e) => {
                        if (!isMobile && e.button === 0) {
                          // Only handle mouse down for desktop (non-mobile)
                          handleMouseDownLeft(e, pIndex, dayIndex);
                        }
                      }}
                      onMouseEnter={() => {
                        if (!isMobile) {
                          // Only handle mouse enter for desktop drag functionality
                          handleMouseEnterLeft(pIndex, dayIndex);
                        }
                      }}
                      onMouseUp={(e) => {
                        if (!isMobile && e.button === 0) {
                          handleMouseUpLeft(e);
                        }
                      }}
                      onTouchEnd={(e) => {
                        if (isMobile) {
                          handleMobileTouch(e);
                        }
                      }}
                      sx={{
                        cursor: "pointer",
                        textAlign: "center",
                        userSelect: "none",
                        WebkitUserSelect: "none", // Safari
                        MozUserSelect: "none", // Firefox
                        msUserSelect: "none", // IE/Edge
                        WebkitTouchCallout: "none", // iOS Safari - prevents callout on long press
                        touchAction: "manipulation", // Prevents zoom on double tap
                        bgcolor: bgColor,
                        color: textColor,
                        p: "1px",
                        width: columnsWidths.days,
                        minWidth: columnsWidths.days,
                        maxWidth: columnsWidths.days,
                        fontWeight: isAssigned ? "bold" : "normal",
                        // Add touch feedback for mobile
                        ...(isMobile && {
                          "&:active": {
                            bgcolor: "action.selected",
                            transform: "scale(0.98)",
                            transition: "all 0.1s ease",
                          },
                        }),
                      }}
                      title={
                        isMobile
                          ? t(
                              "Single tap: toggle unavailable, Double tap: assign shift",
                            )
                          : t(
                              "Left click: toggle unavailable, Right click: assign shift",
                            )
                      }
                    >
                      {getShiftLabel(cellValue) || "\u00A0"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {/* TOTAL ROW */}
            <TableRow
              sx={{
                bgcolor: totalRowIsFull ? "black" : "white",
                color: totalRowIsFull ? "white" : "black",
                "& .MuiTableCell-root": {
                  border: "1px solid #ddd",
                  fontWeight: "bold",
                },
              }}
            >
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.drag),
                  width: columnsWidths.drag,
                  minWidth: columnsWidths.drag,
                  maxWidth: columnsWidths.drag,
                  textAlign: "center",
                }}
              />
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.edit),
                  width: columnsWidths.edit,
                  minWidth: columnsWidths.edit,
                  maxWidth: columnsWidths.edit,
                  textAlign: "center",
                }}
              />
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.name),
                  width: columnsWidths.name,
                  minWidth: columnsWidths.name,
                  maxWidth: columnsWidths.name,
                }}
              >
                <b>
                  {t("total")} ({t("max")}: {totalMaxShifts})
                </b>
              </TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.total),
                  width: columnsWidths.total,
                  minWidth: columnsWidths.total,
                  maxWidth: columnsWidths.total,
                  textAlign: "center",
                }}
              >
                {totalAllShifts}
              </TableCell>
              <TableCell
                style={{
                  ...stickyColumnStyle(stickyOffsets.holiday),
                  width: columnsWidths.holiday,
                  minWidth: columnsWidths.holiday,
                  maxWidth: columnsWidths.holiday,
                  textAlign: "center",
                }}
              >
                {totalHolidayShifts}
              </TableCell>
              {dailyTotals.map((dayTotal, dayIndex) => {
                let cellBgColor = "white";
                let textColor = "black";

                if (dayTotal === shifts.length) {
                  cellBgColor = "#333"; // dark gray
                  textColor = "#fff"; // white
                }

                return (
                  <TableCell
                    key={dayIndex}
                    style={{
                      backgroundColor: cellBgColor,
                      color: textColor,
                      width: columnsWidths.days,
                      minWidth: columnsWidths.days,
                      maxWidth: columnsWidths.days,
                      textAlign: "center",
                      verticalAlign: "middle",
                    }}
                  >
                    {dayTotal}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ScheduleTable;
